import { ThumbsUp, ThumbsDown, Link } from "@phosphor-icons/react";
import { CopyButton } from "~/components/assistant/CopyButton";
import {
  POSITIVE_IMPACT_LABELS,
  NEGATIVE_IMPACT_LABELS,
} from "~/components/assistant/impact-options";
import type { Message } from "~/components/assistant/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface MessageFeedbackProps {
  message: Message;
  groupId?: string;
}

function getForegroundStyles(isPositive: boolean) {
  return isPositive ? "text-green-700" : "text-red-700";
}

export function MessageFeedback({ message, groupId }: MessageFeedbackProps) {
  const currentFeedback = message.messageFeedback;

  // Only show if there's actual feedback
  if (!currentFeedback) {
    return null;
  }

  const currentValue = currentFeedback.feedback_value;
  const isPositive = currentValue === 1;

  // URL is relative to the current shared conversation page
  const feedbackUrl = message.traceId
    ? `${window.location.origin}${window.location.pathname}?turnId=${message.traceId}`
    : groupId
      ? `${window.location.origin}${window.location.pathname}#${groupId}`
      : "";

  return (
    <div className="text-xs px-3 py-2">
      <div className={cn("flex items-center gap-2", getForegroundStyles(isPositive))}>
        {currentValue === 1 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block align-middle">
                <ThumbsUp size={14} weight="fill" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>User found this helpful</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block align-middle">
                <ThumbsDown size={14} weight="fill" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>User found this not helpful</p>
            </TooltipContent>
          </Tooltip>
        )}
        <span className="font-semibold">User Feedback</span>
        {currentFeedback.impact != null && (
          <span className="text-xs font-normal">
            &mdash;{" "}
            {isPositive
              ? POSITIVE_IMPACT_LABELS[currentFeedback.impact]
              : NEGATIVE_IMPACT_LABELS[currentFeedback.impact]}
          </span>
        )}
        {feedbackUrl && (
          <CopyButton
            text={feedbackUrl}
            icon={Link}
            tooltipText="Copy link to this feedback"
            variant="light"
            size="sm"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        )}
      </div>
      {currentFeedback.text && (
        <div className={cn("text-xs mt-1", getForegroundStyles(isPositive))}>
          &ldquo;{currentFeedback.text}&rdquo;
        </div>
      )}
    </div>
  );
}
